import { useQuery } from '@apollo/client';
import React from 'react';
import { ApolloProviderV4 } from '~/apollo/client-v4';
import { graphql } from '~/apollo/generated/v4';
import { type OutcropPageNavigationQuery } from '~/apollo/generated/v4/graphql';
import type { NavLinkProps } from '~/components/common/NavLink';
import { NavLink } from '~/components/common/NavLink';
import { Tooltip } from '~/components/common/Tooltip';
import { Badge } from '~/components/ui/badge';
import { Menu } from '~/components/ui/menu';
import * as routes from '~/paths';

const OUTCROP_PAGE_NAVIGATION = graphql(`
  query OutcropPageNavigation($id: ID!) {
    outcropGet(id: $id) {
      id
      virtualOutcropModels {
        id
      }
      pictures {
        id
      }
      crossSections {
        id
      }
      facies {
        id
      }
      sedimentaryLogs {
        id
      }
      wellLogs {
        id
      }
      reservoirModels {
        id
      }
      production {
        id
      }
      trainingImages {
        id
      }
      variograms {
        id
      }
      gigaPans {
        id
      }
      fieldPictures {
        id
      }
      miniModels {
        id
      }
      photo360s {
        id
      }
      videos {
        id
      }
      panels {
        id
      }
      architecturalElementCount
      measurementCount

      studies {
        id
        pictures {
          id
          outcropTagId
        }
        crossSections {
          id
          outcropTagId
        }
        facies {
          id
          outcropTagId
        }
        sedimentaryLogs {
          id
          outcropTagId
        }
        wellLogs {
          id
          outcropTagId
        }
        reservoirModels {
          id
          outcropTagId
        }
        production {
          id
          outcropTagId
        }
        trainingImages {
          id
          outcropTagId
        }
        variograms {
          id
          outcropTagId
        }
        gigaPans {
          id
          outcropTagId
        }
      }
    }
  }
`);

type CountBadgeProps = {
  children?: React.ReactNode;
  count: number;
};
function CountBadge({ children, count }: CountBadgeProps) {
  return (
    <div>
      <Badge color="ghost" className="break-words">
        {children ? children : count}
      </Badge>
    </div>
  );
}

function OCNavLink(props: NavLinkProps) {
  return (
    <NavLink {...props} className="flex flex-wrap items-start gap-2">
      {props.children}
    </NavLink>
  );
}

type Outcrop = NonNullable<OutcropPageNavigationQuery['outcropGet']>;
type Study = Outcrop['studies'][number];

type CountableOutcropField = keyof Omit<
  Outcrop,
  | '__typename'
  | 'id'
  | 'studies'
  | 'architecturalElementCount'
  | 'measurementCount'
>;
type CountableStudyField = keyof Omit<
  Study,
  '__typename' | 'id' | 'architecturalElements'
>;

const studySOs: CountableStudyField[] = [
  'pictures',
  'facies',
  'crossSections',
  'sedimentaryLogs',
  'wellLogs',
  'production',
  'reservoirModels',
  'trainingImages',
  'variograms',
  'gigaPans',
];
const outcropSOs: CountableOutcropField[] = [
  ...studySOs,
  'miniModels',
  'photo360s',
  'videos',
];

export function OutcropRouteNavigation({ outcropId }: { outcropId: number }) {
  return (
    <ApolloProviderV4>
      <NavigationLoader outcropId={outcropId} />
    </ApolloProviderV4>
  );
}

function NavigationLoader({ outcropId }: { outcropId: number }) {
  const { data } = useQuery(OUTCROP_PAGE_NAVIGATION, {
    variables: { id: outcropId },
  });

  const outcrop = data?.outcropGet;

  if (!outcrop) return null;
  return <NavigationInner outcropId={outcropId} outcrop={outcrop} />;
}

function NavigationInner({
  outcropId,
  outcrop,
}: {
  outcropId: number;
  outcrop: Outcrop;
}) {
  const outcropObjectCount = (property: CountableOutcropField) =>
    outcrop[property].length ?? 0;

  const taggedObjectCount = (property: CountableStudyField) =>
    outcrop.studies.reduce((acc, study) => {
      const count = study[property].filter(
        obj => obj.outcropTagId === parseInt(outcrop.id),
      ).length;
      return acc + count;
    }, 0);

  function totalObjectCount(
    property: CountableOutcropField & CountableStudyField,
  ) {
    const ocCount = outcropObjectCount(property);
    const tagCount = taggedObjectCount(property);
    return ocCount + tagCount;
  }

  const hasOutcropItems = (property: CountableOutcropField) =>
    outcropObjectCount(property) > 0;

  const hasTaggedItems = (property: CountableStudyField) =>
    taggedObjectCount(property) > 0;

  function hasAnyItems(
    ocProp: CountableOutcropField | null,
    studyProp: CountableStudyField | null,
  ) {
    const ocItems = ocProp ? hasOutcropItems(ocProp) : false;
    const studyItems = studyProp ? hasTaggedItems(studyProp) : false;
    return ocItems || studyItems;
  }

  const hasAnySupportingObjects = outcropSOs
    .map(hasOutcropItems)
    .concat(studySOs.map(hasTaggedItems))
    .reduce((acc, cur) => acc || cur, false);

  const ocStudies = outcrop?.studies ?? [];
  const studyCount = ocStudies.length;

  const measurementCount = outcrop.measurementCount;
  const aeCount = outcrop.architecturalElementCount;

  const hasAnyMeasurements = measurementCount > 0;
  const hasAnyPanels = outcrop.panels.length > 0;

  return (
    <div className="space-y-2">
      <Menu>
        <Menu.Item>
          <OCNavLink to={routes.outcropOverviewTabRoute(outcropId)} end>
            Overview
          </OCNavLink>
        </Menu.Item>
        <Menu.Item>
          <OCNavLink to={routes.outcropKeyParametersTabRoute(outcropId)}>
            Key Parameters
          </OCNavLink>
        </Menu.Item>
        {hasOutcropItems('virtualOutcropModels') && (
          <Menu.Item>
            <OCNavLink to={routes.outcropVirtualOutcropsTabRoute(outcropId)}>
              Virtual Outcrop Models
              <CountBadge count={outcropObjectCount('virtualOutcropModels')} />
            </OCNavLink>
          </Menu.Item>
        )}
        <Menu.Item>
          <OCNavLink to={routes.outcropAnaloguesTabRoute(outcropId)}>
            Analogues
          </OCNavLink>
        </Menu.Item>
        <Menu.Item>
          <OCNavLink to={routes.outcropPalaeogeographyTabRoute(outcropId)}>
            Palaeogeography
          </OCNavLink>
        </Menu.Item>
        {outcrop.studies.length > 0 && (
          <Menu.Item>
            <OCNavLink to={routes.outcropStudiesTabRoute(outcropId)}>
              Studies <CountBadge count={studyCount} />
            </OCNavLink>
          </Menu.Item>
        )}
      </Menu>

      {(hasAnyMeasurements || hasAnyPanels) && (
        <Menu bordered>
          <Menu.Title>Reservoir Modelling Inputs</Menu.Title>
          {hasAnyMeasurements && (
            <Menu.Item>
              <OCNavLink to={routes.outcropMeasurementsTabRoute(outcropId)}>
                AE Measurements{' '}
                <Tooltip
                  message={`${measurementCount} measurements on ${aeCount} architectural elements`}
                >
                  <span>
                    <CountBadge count={measurementCount}>
                      {measurementCount} / {aeCount} AEs
                    </CountBadge>
                  </span>
                </Tooltip>
              </OCNavLink>
            </Menu.Item>
          )}
          {hasAnyPanels && (
            <Menu.Item>
              <OCNavLink to={routes.panelsRoute(outcropId)}>
                Analysed Panels{' '}
                <CountBadge count={outcropObjectCount('panels')} />
              </OCNavLink>
            </Menu.Item>
          )}
        </Menu>
      )}

      {hasAnySupportingObjects && (
        <Menu>
          {hasAnyItems('pictures', 'pictures') && (
            <Menu.Item>
              <OCNavLink to={routes.outcropPicturesTabRoute(outcropId)}>
                Pictures <CountBadge count={totalObjectCount('pictures')} />
              </OCNavLink>
            </Menu.Item>
          )}
          {hasAnyItems('facies', 'facies') && (
            <Menu.Item>
              <OCNavLink to={routes.outcropFaciesTabRoute(outcropId)}>
                Facies <CountBadge count={totalObjectCount('facies')} />
              </OCNavLink>
            </Menu.Item>
          )}
          {hasAnyItems('crossSections', 'crossSections') && (
            <Menu.Item>
              <OCNavLink to={routes.outcropCrossSectionsTabRoute(outcropId)}>
                Cross Sections
                <CountBadge count={totalObjectCount('crossSections')} />
              </OCNavLink>
            </Menu.Item>
          )}
          {hasAnyItems('sedimentaryLogs', 'sedimentaryLogs') && (
            <Menu.Item>
              <OCNavLink to={routes.outcropSedimentaryLogsTabRoute(outcropId)}>
                Sedimentary Logs
                <CountBadge count={totalObjectCount('sedimentaryLogs')} />
              </OCNavLink>
            </Menu.Item>
          )}
          {hasAnyItems('wellLogs', 'wellLogs') && (
            <Menu.Item>
              <OCNavLink to={routes.outcropWellLogsTabRoute(outcropId)}>
                Well Logs <CountBadge count={totalObjectCount('wellLogs')} />
              </OCNavLink>
            </Menu.Item>
          )}
          {hasAnyItems('production', 'production') && (
            <Menu.Item>
              <OCNavLink to={routes.outcropProductionTabRoute(outcropId)}>
                Production <CountBadge count={totalObjectCount('production')} />
              </OCNavLink>
            </Menu.Item>
          )}
          {hasAnyItems('reservoirModels', 'reservoirModels') && (
            <Menu.Item>
              <OCNavLink to={routes.outcropReservoirModelsTabRoute(outcropId)}>
                Reservoir Models
                <CountBadge count={totalObjectCount('reservoirModels')} />
              </OCNavLink>
            </Menu.Item>
          )}
          {hasAnyItems('trainingImages', 'trainingImages') && (
            <Menu.Item>
              <OCNavLink to={routes.outcropTrainingImagesTabRoute(outcropId)}>
                Training Images
                <CountBadge count={totalObjectCount('trainingImages')} />
              </OCNavLink>
            </Menu.Item>
          )}
          {hasAnyItems('variograms', 'variograms') && (
            <Menu.Item>
              <OCNavLink to={routes.outcropVariogramsTabRoute(outcropId)}>
                Variograms <CountBadge count={totalObjectCount('variograms')} />
              </OCNavLink>
            </Menu.Item>
          )}
          {hasAnyItems('gigaPans', 'gigaPans') && (
            <Menu.Item>
              <OCNavLink to={routes.outcropPanoramasTabRoute(outcropId)}>
                Panoramas <CountBadge count={totalObjectCount('gigaPans')} />
              </OCNavLink>
            </Menu.Item>
          )}
          {outcropObjectCount('fieldPictures') > 0 && (
            <Menu.Item>
              <OCNavLink to={routes.outcropFieldPicturesTabRoute(outcropId)}>
                Field Pictures{' '}
                <CountBadge count={outcropObjectCount('fieldPictures')} />
              </OCNavLink>
            </Menu.Item>
          )}
          {outcropObjectCount('miniModels') > 0 && (
            <Menu.Item>
              <OCNavLink to={routes.outcropMiniModelsTabRoute(outcropId)}>
                Mini-Models{' '}
                <CountBadge count={outcropObjectCount('miniModels')} />
              </OCNavLink>
            </Menu.Item>
          )}
          {outcropObjectCount('photo360s') > 0 && (
            <Menu.Item>
              <OCNavLink to={routes.outcropPhoto360sTabRoute(outcropId)}>
                360 Photos{' '}
                <CountBadge count={outcropObjectCount('photo360s')} />
              </OCNavLink>
            </Menu.Item>
          )}
          {outcropObjectCount('videos') > 0 && (
            <Menu.Item>
              <OCNavLink to={routes.outcropVideosTabRoute(outcropId)}>
                Videos <CountBadge count={outcropObjectCount('videos')} />
              </OCNavLink>
            </Menu.Item>
          )}
        </Menu>
      )}
    </div>
  );
}
